import { Container, Typography } from '@material-ui/core';
import React from 'react';

import Button from '@components/common/button';
import Figures, { Circle, Image } from '@components/common/figures';
import ListWithListStyleImage from '@components/common/list-with-list-style-image';
import useStyles from '@views/probate/styles';

const ProbateView: React.FC = () => {
  const classes = useStyles();

  const topImages: Image[] = [
    {
      src: '/png/sofa.png',
      classWrapper: classes.sofa,
    },
    {
      src: '/png/person-with-calculator.png',
      classWrapper: classes.personWithCalculator,
    },
    {
      src: '/png/cactus.png',
      classWrapper: classes.cactus,
    },
  ];

  const topCircle: Circle[] = [
    {
      background: 'blueGradient',
      classWrapper: classes.topCircle,
    },
  ];

  const serviceImage: Image[] = [
    {
      src: '/png/person-hand-up.png',
      classWrapper: classes.servicePerson,
    },
  ];

  const serviceCircles: Circle[] = [
    {
      background: 'pink',
      classWrapper: classes.serviceCircleOne,
    },
    {
      background: 'blueGradient',
      classWrapper: classes.serviceCircleTwo,
    },
  ];

  const probateImage: Image[] = [
    {
      src: '/png/mailbox.png',
      classWrapper: classes.mailbox,
    },
  ];

  const probateCircles: Circle[] = [
    {
      background: 'pinkMain',
      classWrapper: classes.probateCircleOne,
    },
    {
      background: 'pink',
      classWrapper: classes.probateCircleTwo,
    },
  ];

  const probateBenefits = [
    {
      title: 'Why choose our fixed-fee probate service?',
      description: `Our estate administration experts have well over a decade of experience helping 
        thousands of families in the UK distributing inheritance in the most cost effective and timely manner.`,
    },
    {
      title: 'Costs. Fixed With no Hidden Surprises',
      description: `With access to full STEP (The Society for Trust and Estate Practitioners) qualified advisers 
        that will administer the entirety of your estate no matter how complex for the small fixed fee of 1.5%.`,
    },
    {
      title: "No Deposit. There's nothing to pay up front",
      description: `Leaving your family with the burden of paying something upfront during a difficult time is 
        the last thing you should worry about. There is nothing to pay initially with costs deducted later on.`,
    },
    {
      title: 'We will take care of every little bit',
      description: `Traditionally most probate solicitors will only do the minimum in terms of applying for 
        grant of probate, we will ensure the full administration is done in a timely manner.`,
    },
    {
      title: 'You and your family are protected',
      description: `When we settle an estate, our probate service also takes on full legal and financial 
        responsibility. You're fully covered, at no extra cost.`,
    },
    {
      title: 'Ensure Inheritance is expedited to your loved ones',
      description: `Administration is started immediately as we are providing a professional executor service. 
        Grant of probate is applied for and we can then start to request the release of assets.`,
    },
    {
      title: 'Never left in the dark',
      description: `Your loved ones will be kept up to date with every step of the administration process through 
        their very own personal representative and every step of the process will be explained in an empathetic 
        and professional manner.`,
    },
  ];

  const benefitImage: Image[] = [
    {
      src: '/png/person-thumb-up-large.png',
      classWrapper: classes.benefitPerson,
    },
  ];

  const benefitCircle: Circle[] = [
    {
      background: 'blueGradient',
      classWrapper: classes.benefitCircle,
    },
  ];

  const simpwillServices = [
    {
      mainTitle: 'Simpwill Additional Services',
      innerData: [
        {
          title: 'Mitigating tax exposure',
          description: `Our experts tailor advice to meet your specific needs providing the best specialist tax advice`,
        },
        {
          title: 'We can setup trusts in your will',
          description: `With our help, we can set up various trusts to make sure your loved ones are provided for in 
            the future and protect your estate from unnecessary inheritance tax`,
        },
        {
          title: 'Deed of variation',
          description: `We can help you make changes to a person's will after their death`,
        },
        {
          title: "Dealing with deceased person's Assets & Property",
          description: `We offer a stress-free service to handle all requirements for inherited property`,
        },
        {
          title: 'Settling Tax affairs',
          description: `No need to stress about completing your income tax, our advisors are here to handle all of your 
            returns for the tax-year`,
        },
      ],
    },
    {
      mainTitle: 'Maximise Inheritance',
      innerData: [
        {
          title: 'Beneficiary tracing',
          description: `Our specialist team can assist in locating missing or unknown beneficiaries`,
        },
        {
          title: 'Asset auditing and valuation services',
          description: `Here at Simpwill, we can provide you with a detailed asset valuation, from furniture and 
            possessions, to special items`,
        },
        {
          title: 'Repatriation of overseas assets',
          description: `By instructing us to repatriate overseas assets, we will offer invaluable insights into some 
            of the issues you may encounter when moving money home`,
        },
        {
          title: 'Indemnity for lost share certificates',
          description: `Our insight and expertise makes us the right fit to assist with indemnities for lost 
            share certificates`,
        },
      ],
    },
    {
      mainTitle: 'Supporting and helping you',
      innerData: [
        {
          title: 'Organising postal redirection',
          description: `We will take care of any postal redirection, so you don't have to worry`,
        },
        {
          title: 'Cancelling accounts and services ',
          description: `So you don't have to worry about the small things, our team of experts are on hand to 
            cancel all utilities linked to the deceased's property`,
        },
        {
          title: 'Professional vacant property insurance',
          description: `We can provide vacant property insurance which is arranged and paid for by our experts 
            for 12 months`,
        },
      ],
    },
  ];

  return (
    <div className="overflow-hidden">
      <section className={classes.topSection}>
        <Container maxWidth={false} className={classes.relative}>
          <div className={classes.wishes}>
            <Typography component="h1" variant="h3" className={classes.title}>
              Simpwill: Probate &amp; Estate Administration
              <span className={classes.blueDot}>.</span>
            </Typography>
            <Typography variant="body2">
              At Simpwill we have made it simpler than ever to arrange probate
              and handle all estate administration with ease, alleviating the
              burden from your family or friends.
            </Typography>

            <Button
              id="start-will-button-probate"
              className={classes.startWillButton}
              styleType="containedBlue"
              text="Start will"
              fullWidthOnMobile
              href="/create-will/poll"
            />
          </div>

          <Figures
            images={topImages}
            circles={topCircle}
            classWrapper={classes.topFigures}
          />

          <div className={classes.countrywide}>
            <img
              src="/png/partners/country-wide-color.png"
              alt="country-wide"
            />
            <Typography variant="h3">
              We have partnered with <br /> Countrywide Tax &amp; Trust
              Corporation
            </Typography>
          </div>
        </Container>
      </section>

      <section className={classes.administrators}>
        <Container maxWidth={false}>
          <Typography variant="body2">
            The UK&apos;s leading professional estate administrators.
          </Typography>
          <Typography variant="body2">
            Simpwill have selected Countrywide Tax and Trust Corporation to
            handle all professional executor, trustee and probate services. They
            are also Simpwill&apos;s chosen partner for further estate planning
            and asset protection. As a customer of Simpwill you will gain access
            to a dedicated team of STEP (Society of Trust and Estate
            Practitioners) qualified advisers, solicitors and conveyancers!
          </Typography>
        </Container>
      </section>

      <section className={classes.service}>
        <Container maxWidth={false} className={classes.relative}>
          <div className={classes.serviceContent}>
            <Typography variant="h3">Executor Service</Typography>
            <Typography variant="body2">
              Probate and Estate Administration all handled by the professionals
              with no upfront cost.
            </Typography>
            <br />
            <Typography variant="body2">
              Feel safe in the knowledge that Simpwill have organised
              administration and probate, meaning your family do not need to
              concern themselves with the legal, tax and financial work involved
              at an understandably difficult period.
            </Typography>
          </div>

          <Figures
            images={serviceImage}
            circles={serviceCircles}
            classWrapper={classes.serviceFigures}
          />
        </Container>
      </section>

      <section className={classes.executor}>
        <Container maxWidth={false} className={classes.executorsContent}>
          <div>
            <Typography variant="subtitle1">
              What is an executor and what do they do?
            </Typography>
            <ListWithListStyleImage
              image="/png/icons/circle.png"
              list={[
                `Executors are the people you appoint in your Will to deal with the 
                  administration of your estate after your death.`,
                `Your chosen executors will have a very arduous job ahead of them, 
                  administrating a person's estate is by no means a small feat. Settling an estate carries 
                  considerable responsibilities which can often be complicated, difficult and time-consuming.`,
                `Whoever is responsible will have a wide range of duties and the legal right to administer your 
                  estate. They will also have to work out the amount of inheritance tax owed on the estate and prepare 
                  the tax return to pay it. A professional executor can be appointed to take on these duties.`,
              ]}
            />
          </div>
          <div className={classes.verticalBorder} />
          <div>
            <Typography variant="subtitle1">
              Why use a professional executor?
            </Typography>
            <ListWithListStyleImage
              image="/png/icons/check.png"
              list={[
                `An independent professional executor will ensure the instructions set out in your will are 
                  followed, which can be complex and time consuming.`,
                `Appointing a professional to deal with your estate can ease the pressure on loved ones 
                  at a difficult time.`,
                `Inheritance tax may be payable on your estate. The rules are quite complicated, but our professional 
                  executor service will help to ensure that the correct amount is paid and they will administer 
                  your estate in the most tax efficient way.`,
              ]}
            />
          </div>
        </Container>
      </section>

      <section className={classes.services}>
        <Container maxWidth={false} className={classes.relative}>
          <Typography variant="h3">Probate Services</Typography>
          <div className={classes.servicesCards}>
            <div className={classes.servicesCard}>
              <div className={classes.serviceIcon}>
                <i className="icon-form-doc" />
              </div>
              <div className={classes.serviceText}>
                <Typography variant="subtitle1">What is probate?</Typography>
                <Typography variant="body2">
                  It is a legal document that will give the executors of the
                  will the relevant authority to deal with the assets and carry
                  out the wishes of the loved one who has sadly passed away.
                </Typography>
              </div>
            </div>
            <div className={classes.servicesCard}>
              <div className={classes.serviceIcon}>
                <i className="icon-plan" />
              </div>
              <div className={classes.serviceText}>
                <Typography variant="subtitle1">How does it work?</Typography>
                <Typography variant="body2">
                  The process of Probate can vary from case to case and can be
                  extremely difficult to carry out, because every will and the
                  assets within are different. Simpwill have appointed the very
                  best in probate professionals to handle the administration of
                  your estate.
                </Typography>
              </div>
            </div>
            <div className={classes.servicesCard}>
              <div className={classes.serviceIcon}>
                <i className="icon-collaboration" />
              </div>
              <div className={classes.serviceText}>
                <Typography variant="subtitle1">What can we offer?</Typography>
                <Typography variant="body2">
                  We will deal with the estate from start to finish; establish
                  the assets and liabilities; obtain the Grant of Probate;
                  settle any taxes due; encash or transfer assets including
                  property; settle any debts; make payments to beneficiaries;
                  and prepare the estate accounts.
                </Typography>
              </div>
            </div>
          </div>
          <Figures
            images={probateImage}
            circles={probateCircles}
            classWrapper={classes.probateFigures}
          />
        </Container>
      </section>

      <section className={classes.benefits}>
        <Container maxWidth={false} className={classes.relative}>
          <div className={classes.benefitContent}>
            <Typography variant="h3">
              Simpwill Probate <br /> Benefits
            </Typography>
            {probateBenefits.map((item, index) => (
              <div className={classes.benefit} key={item.title}>
                <Typography variant="subtitle1">
                  <span>0{index + 1}.&nbsp;</span>
                  {item.title}
                </Typography>
                <Typography variant="body2">{item.description}</Typography>
              </div>
            ))}
          </div>
          <Figures
            images={benefitImage}
            circles={benefitCircle}
            classWrapper={classes.benefitsFigures}
          />
        </Container>
      </section>

      <section className={classes.simpwillServices}>
        <Container maxWidth={false}>
          <Typography variant="h3">
            Our offer also includes next services:
          </Typography>
          <div className={classes.simpwillServicesContent}>
            {simpwillServices.map((service, index) => (
              <React.Fragment key={service.mainTitle}>
                <div className={classes.simpwillService}>
                  <ListWithListStyleImage
                    boldTitle
                    image="/png/icons/check.png"
                    list={[`${service.mainTitle}`]}
                  />
                  {service.innerData.map((data) => (
                    <div key={data.title} className={classes.SimpwillInnerData}>
                      <Typography variant="body1">{data.title}</Typography>
                      <Typography variant="body2">
                        {data.description}
                      </Typography>
                    </div>
                  ))}
                </div>
                {index === 0 && <div className={classes.verticalBorder} />}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </section>

      <section className={classes.support}>
        <Container maxWidth={false}>
          <div className={classes.supportContent}>
            <Typography variant="subtitle1">Do you need support?</Typography>
            <Button
              id="live-chat-button-probate"
              styleType="containedBlue"
              text="Live chat"
              fullWidthOnMobile
              onClick={() => window.tidioChatApi.open()}
            />
          </div>
        </Container>
      </section>
    </div>
  );
};

export default ProbateView;
